import { Delete, Grid3x3, Phone } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { Callout } from "@/components/ui/feedback";
import { Input } from "@/components/ui/input";
import { Tooltip } from "@/components/ui/misc";
import { ApiError } from "@/lib/api";
import { chosenCampaign, dialPathText } from "@/lib/manual-call";
import { cn } from "@/lib/utils";
import { DialTargetPicker } from "@/softphone/dial-target";
import { CampaignPicker, useOutboundDial } from "@/softphone/manual-call";
import { seesCampaigns } from "@/lib/types";
import { useAuth } from "@/lib/auth";

/**
 * The dialler, and the twelve keys under it.
 *
 * Lives here rather than in the dock because it is on two surfaces now — the dock's idle and
 * "add call" views, and the "Dial now" box on the phone page — and a rep who has learnt that the
 * delete key is in the field, or that Enter dials, should not have to learn it twice. One
 * component, two layouts: `stacked` is the dock's 320px column, `inline` puts the field and Call
 * on one row for a full-width card.
 *
 * Two ways to say who: type it, or pick a colleague. The picker is `dial-target.tsx` and is on
 * every surface that dials, because an extension is four digits with no meaning and the console
 * had nowhere to look one up. Choosing somebody dials them — the field is for the numbers a list
 * cannot hold.
 *
 * **An outside number goes out through the rep's campaign** when they are signed in to one and
 * between calls — Ready, on a break or in wrap-up (ADR-047 and ADR-068 in the platform repo,
 * `useOutboundDial`): the dialer places it and the ACD brings the answer back to this phone, in
 * the call's room. The line under the field says so before the rep presses Call, names the
 * campaign, offers a choice when there are several, and tells a rep on a break they go back on
 * it after. An extension is never sent that way, and the line says that too. A refusal — signed
 * out after all, the do-not-call list — is shown right here, with the number kept.
 *
 * **The keypad is the dock's, not the page's.** It exists for the surface a rep reaches with a
 * thumb while a call is up; the phone page is a full-width card in front of a keyboard, where
 * twelve buttons are a slower way to type a number than typing it. So `inline` renders no keypad
 * and no toggle for one — through `keypadOffered`, a real choice rather than a shorthand for the
 * layout, because a future narrow surface could want the stacked keys without being the dock.
 */

const KEYPAD_PREF_KEY = "acl.softphone.keypad";

function readKeypadPref(): boolean {
  try {
    return localStorage.getItem(KEYPAD_PREF_KEY) === "true";
  } catch {
    return false;
  }
}

function writeKeypadPref(shown: boolean): void {
  try {
    localStorage.setItem(KEYPAD_PREF_KEY, String(shown));
  } catch {
    /* non-fatal — the preference simply will not survive a reload */
  }
}

/**
 * A form, so Enter dials and a screen reader announces it as one. Where the keypad is offered it
 * is a preference rather than a state — a rep with a headset types, a rep on a touch screen taps,
 * and neither wants to make that choice again every call.
 *
 * `phone.dial` parks the call in progress before it places the new one; that rule belongs to the
 * phone, not to this button, so nothing here does it.
 */
export function Dialler({
  layout = "stacked", keypadOffered = true, disabled = false, disabledReason,
}: {
  layout?: "stacked" | "inline";
  /**
   * Whether this surface offers the twelve keys at all. Off for the phone page's card, which
   * sits in front of a keyboard — see the module docstring. Its own prop rather than
   * `layout === "stacked"` because the two are separate questions: the layout is how wide the
   * surface is, this is whether tapping beats typing on it.
   */
  keypadOffered?: boolean;
  /** The phone cannot place a call right now; `disabledReason` says why, under the field. */
  disabled?: boolean;
  disabledReason?: string;
}) {
  const out = useOutboundDial();
  const { user } = useAuth();
  // A rep neither chooses nor reads the campaign: core-api picks it (ACL-652).
  const sees = seesCampaigns(user?.role);
  const id = React.useId();
  const campaignFieldId = React.useId();
  const [destination, setDestination] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [refusal, setRefusal] = React.useState<string | null>(null);
  const [keypadPref, setKeypadPref] = React.useState(readKeypadPref);
  const inline = layout === "inline";
  const keypad = keypadOffered && keypadPref;

  // Where this number goes, decided as the rep types: through a campaign, or from the phone.
  const path = out.path(destination);
  const campaigns = sees && path.via === "campaign" ? path.campaigns : [];
  const [picked, setPicked] = React.useState<string | null>(null);
  const chosen =
    (picked && campaigns.find((c) => c.campaign_id === picked)) ||
    chosenCampaign(campaigns, out.remembered);
  const mustChoose = sees && path.via === "campaign" && chosen === null;
  const pathText = dialPathText(path, chosen, !sees);

  const dial = async (target: string = destination) => {
    if (!target.trim() || busy || disabled) return;
    setBusy(true);
    setRefusal(null);
    try {
      await out.place(target, chosen?.campaign_id ?? null);
      if (chosen) out.remember(chosen.campaign_id);
      setDestination("");
    } catch (e) {
      // A refusal from core-api has a sentence for the rep and is shown here, under the number,
      // which stays in the field so "try again" is one click. A refusal from the phone itself
      // has already gone into the banner at the top of the dock, as it always has.
      if (e instanceof ApiError) setRefusal(e.message);
    } finally {
      setBusy(false);
    }
  };

  const toggleKeypad = () => {
    const next = !keypad;
    setKeypadPref(next);
    writeKeypadPref(next);
  };

  // One element, rendered in one of two places — beside the field on the wide layout, under
  // everything on the dock's.
  const call = (
    <Button
      type="submit"
      variant="default"
      className={cn("h-10 text-sm", inline ? "px-5" : "w-full")}
      loading={busy}
      disabled={disabled || !destination.trim() || mustChoose}
    >
      <Phone />
      Call
    </Button>
  );

  // Which campaign an outside number goes through, when the rep can use more than one. Under
  // the field rather than in it: the number is what they are typing, and this is a fact about
  // where it goes that most reps will set once and never touch again.
  const campaignPicker = campaigns.length > 1 && (
    <CampaignPicker
      id={campaignFieldId}
      campaigns={campaigns}
      value={chosen?.campaign_id ?? null}
      onChange={(value) => {
        setPicked(value);
        out.remember(value);
      }}
      disabled={disabled || busy}
    />
  );

  const field = (
    <Input
      id={id}
      value={destination}
      onChange={(e) => {
        setDestination(e.target.value);
        if (refusal) setRefusal(null);
      }}
      placeholder="Number or extension"
      className="h-10 font-mono text-base"
      inputMode="tel"
      autoComplete="off"
      disabled={disabled}
      // In the field rather than in the keypad grid, because a rep who mistypes wants to
      // correct it where the number is — whether they tapped the digits or typed them, and
      // whether the keypad is open or not.
      //
      // ALWAYS rendered, disabled while there is nothing to delete, and that is load-bearing:
      // `Input` returns a bare <input> with no trailing element and a wrapped one with it, so
      // a button that appeared on the first keystroke would change the element type at that
      // position and React would remount the field — dropping focus and the caret mid-number.
      trailing={
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          disabled={disabled || destination.length === 0}
          // Keeps the caret in the field, so a rep can delete several digits and carry on
          // typing without clicking back into it.
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setDestination((v) => v.slice(0, -1))}
          aria-label="Delete the last digit"
        >
          <Delete />
        </Button>
      }
    />
  );

  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        void dial();
      }}
    >
      <label htmlFor={id} className="sr-only">
        Number or extension to call
      </label>
      {/* Wrapping only on the wide layout. In the dock's 320px column the field is `w-full`,
          so `flex-wrap` would put the keypad toggle on a line of its own instead of letting the
          field shrink beside it. */}
      <div className={cn("flex gap-1.5", inline && "flex-wrap")}>
        {/* `Input` sizes itself to whatever holds it. On the wide layout Call shares the row, so
            the field needs a floor to shrink against — without one a narrow window squeezes it
            to nothing instead of dropping Call onto the next line. */}
        {inline ? <div className="min-w-40 flex-1">{field}</div> : field}
        {/* Before the keypad toggle: picking a person is the commoner act, and on the dock's
            320px row the two buttons sit together at the end of the field. */}
        <DialTargetPicker disabled={disabled || busy} onPick={(ext) => void dial(ext)} />
        {keypadOffered && (
          <Tooltip content={keypad ? "Hide the keypad" : "Show the keypad"}>
            <Button
              type="button"
              variant={keypad ? "subtle" : "outline"}
              size="icon"
              className="size-10 shrink-0"
              onClick={toggleKeypad}
              aria-pressed={keypad}
              aria-label="Keypad"
            >
              <Grid3x3 />
            </Button>
          </Tooltip>
        )}
        {inline && call}
      </div>

      {keypad && <Keypad onPress={(d) => setDestination((v) => v + d)} disabled={disabled} />}

      {campaignPicker}
      {pathText && !disabled && (
        <p className="text-xs text-text-muted" aria-live="polite">
          {pathText}
        </p>
      )}
      {refusal && <Callout tone="danger">{refusal}</Callout>}

      {!inline && call}

      {disabled && disabledReason && <p className="text-xs text-text-muted">{disabledReason}</p>}
    </form>
  );
}

const KEYS = [
  ["1", ""], ["2", "ABC"], ["3", "DEF"],
  ["4", "GHI"], ["5", "JKL"], ["6", "MNO"],
  ["7", "PQRS"], ["8", "TUV"], ["9", "WXYZ"],
  ["*", ""], ["0", "+"], ["#", ""],
] as const;

/** How the two symbols are read out; a digit is its own name. */
const KEY_NAMES: Record<string, string> = { "*": "Star", "#": "Pound" };

/** Twelve keys and nothing else. Deleting belongs to whatever the keys are feeding: the
 *  dialler puts it in its field, and a sent DTMF tone cannot be taken back at all. */
export function Keypad({
  onPress, tall = false, disabled = false, className,
}: {
  onPress: (digit: string) => void;
  tall?: boolean;
  /** Keys whose target cannot take them — dimmed rather than removed, so the grid does not
   *  vanish and reappear under a hand that is reaching for it. */
  disabled?: boolean;
  className?: string;
}) {
  return (
    <div className={cn("grid grid-cols-3 gap-1.5", className)} role="group" aria-label="Keypad">
      {KEYS.map(([digit, letters]) => (
        <button
          key={digit}
          type="button"
          onClick={() => onPress(digit)}
          disabled={disabled}
          aria-label={KEY_NAMES[digit] ?? digit}
          className={cn(
            "flex flex-col items-center justify-center rounded-md border border-border bg-surface",
            "transition-colors hover:bg-surface-hover active:bg-gray-4",
            "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-surface",
            tall ? "h-12" : "h-10",
          )}
        >
          <span className="font-mono text-base leading-none text-text">{digit}</span>
          {letters && (
            <span
              className="mt-1 text-[10px] leading-none tracking-[0.15em] text-text-subtle"
              aria-hidden
            >
              {letters}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}
